import { Button } from '@/shared/ui/button';

import { describeFieldSource } from '../field-object-labels';

import { FieldPanelHeading, FieldStatus, FieldTime } from './field-primitives';
import { FieldReports } from './field-reports';

import './field-objects.css';

import type { FieldPanelProps } from './field-types';

type Destination = 'board' | 'reviews' | 'documents' | 'operations' | 'engagement';
const gateGuidance: Record<string, { text: string; destination: Destination; action: string }> = {
  'agreed-scope': {
    text: 'Confirm the scope with its owner and resolve or explicitly defer high-impact decisions.',
    destination: 'engagement',
    action: 'Review decisions',
  },
  'delivered-outcomes': {
    text: 'Share the current candidate with Jordan and demonstrate the agreed user journeys.',
    destination: 'reviews',
    action: 'Review delivery evidence',
  },
  'operational-resilience': {
    text: 'Demonstrate trusted prevention or evidence of incident recovery on the current candidate.',
    destination: 'operations',
    action: 'Review operational evidence',
  },
  'final-day': {
    text: 'Complete the ten-day engagement and reach the final checkpoint.',
    destination: 'board',
    action: 'Open tickets',
  },
  'release-evidence': {
    text: 'Merge the current candidate, complete its trusted verification, and resolve the release blockers.',
    destination: 'operations',
    action: 'Review release requirements',
  },
  released: {
    text: 'Request release approval for the current candidate and confirm it was released to the synthetic cohort.',
    destination: 'operations',
    action: 'Open release',
  },
  change: {
    text: 'Record your change decision as a delivery using the Decision record type.',
    destination: 'documents',
    action: 'Open deliveries',
  },
  incident: {
    text: 'Resolve the incident with at least two response updates, and record your incident review as a delivery.',
    destination: 'operations',
    action: 'Open incidents',
  },
  handoff: {
    text: 'Record your handoff as a delivery and complete a trusted rollback check for the current candidate.',
    destination: 'documents',
    action: 'Open deliveries',
  },
};

export function FieldAssessment({
  snapshot,
  busy,
  onAction,
  onNavigate,
}: FieldPanelProps & { onNavigate?: (destination: Destination) => void }) {
  const assessment = snapshot.assessment;
  const gates = snapshot.progress?.readiness.gates ?? assessment.gates;
  const blockers =
    snapshot.progress?.readiness.blockerCount ?? gates.filter((gate) => !gate.passed).length;
  const pending = snapshot.operations.some(
    (operation) =>
      operation.kind === 'assessment' && ['pending', 'running'].includes(operation.status),
  );
  return (
    <div className="field-object-panel h-full overflow-y-auto">
      <FieldPanelHeading title="Assessment" tourTarget="assessment">
        See what your evidence establishes and what still needs attention. Final readiness is
        moderated by an instructor.
      </FieldPanelHeading>
      <div className="space-y-6 p-5">
        <div className="field-assessment-overview">
          <div className="min-w-0 flex-1">
            <FieldStatus value={assessment.status} />
            <h3 className="mt-3 text-xl font-semibold">
              {gates.length
                ? blockers === 0
                  ? 'Ready for final review'
                  : `Not ready: ${blockers} ${blockers === 1 ? 'blocker' : 'blockers'}`
                : 'Readiness evidence is not available yet'}
            </h3>
            {assessment.evaluatedAt ? (
              <p className="text-fg-muted mt-3 text-sm">
                Last checked <FieldTime value={assessment.evaluatedAt} />
              </p>
            ) : (
              <p className="text-fg-muted mt-2 text-sm leading-6">
                Readiness updates with your work. Request a formal check when you are ready for
                review.
              </p>
            )}
            {assessment.status === 'completed' && assessment.score !== null ? (
              <p className="mt-3 text-sm">
                Moderated score <strong>{assessment.score} / 100</strong>
              </p>
            ) : null}
          </div>
          <Button
            disabled={busy || pending}
            onClick={() => void onAction({ type: 'assessment.request' })}
          >
            {pending ? 'Checking readiness…' : 'Check readiness'}
          </Button>
        </div>
        <p className="text-fg-muted text-sm leading-6">
          {snapshot.discovery
            ? 'Check readiness whenever the evidence is ready. Assessment considers what you delivered, how you established the requirements, and how you handled uncertainty. Ordinary stakeholder conversations are part of the work.'
            : 'You can check evidence at any stage. Final moderation requires the day 10 checkpoint and all required evidence. A completed checklist alone does not establish competence.'}
        </p>
        {assessment.feedback ? (
          <p className="whitespace-pre-wrap text-sm leading-7">{assessment.feedback}</p>
        ) : null}
        <section aria-label="Readiness gates">
          <h3 className="mb-3 text-base font-semibold">Required evidence</h3>
          {gates.length ? (
            <ol>
              {gates.map((gate, index) => (
                <li key={gate.id} className="field-gate-row">
                  <span className="field-gate-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h4 className="text-sm font-semibold">{gate.label}</h4>
                      <span className="text-fg-muted text-xs">
                        {gate.passed ? 'Satisfied' : 'Needs evidence'}
                      </span>
                    </div>
                    {gateGuidance[gate.id] ? (
                      <p className="text-fg-muted mt-2 text-sm leading-6">
                        {gateGuidance[gate.id]?.text}
                      </p>
                    ) : null}
                    {!gate.passed && onNavigate && gateGuidance[gate.id] ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          const guidance = gateGuidance[gate.id];
                          if (guidance) onNavigate(guidance.destination);
                        }}
                      >
                        {gateGuidance[gate.id]?.action}
                      </Button>
                    ) : null}
                    {gate.evidenceIds.length ? (
                      <details className="text-fg-muted mt-2 text-sm">
                        <summary className="cursor-pointer">
                          Evidence considered ({gate.evidenceIds.length})
                        </summary>
                        <ul className="space-y-2 pb-2">
                          {gate.evidenceIds.map((id) => (
                            <li key={id} title={id}>
                              {describeFieldSource(snapshot, id)}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-fg-muted text-sm leading-6">
              Readiness gates will appear when the current evidence is available. Missing or
              unverified execution results never count as a pass.
            </p>
          )}
        </section>
        <FieldReports snapshot={snapshot} />
      </div>
    </div>
  );
}
